<template>
  <div class="jps" :class="tooltipClass">
    <span ref="trigger" class="trigger" @click="toggle">
      <slot name="trigger" />
    </span>
    <ClientOnly>
      <Teleport to="#layer-tooltip">
        <Transition :name="transition">
          <div ref="tooltip" class="jps-tooltip" :class="tooltipClass" v-show="isActive" aria-modal="true" v-trap-focus="true">
            <slot />
            <div class="jps-tooltip-arrow" data-popper-arrow="" />
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import vTrapFocus, { focusFirst } from '~/directives/trap-focus'
import { createPopper } from '@popperjs/core'
import type { Placement } from '@popperjs/core'

interface Props {
  placement?: Placement
  tooltipClass?: string
  transition?: string
  otherToggle?: HTMLElement
}
const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  transition: 'fade',
  otherToggle: null
})

const isActive: Ref<boolean> = ref(false)
const targetToggle = computed<HTMLElement>(() => {
  if (props.otherToggle) {
    return props.otherToggle
  } else {
    return trigger.value
  }
})
let popperJS

const trigger: Ref<HTMLElement> = ref()
const tooltip: Ref<HTMLElement> = ref()
onMounted(async () => {
  await nextTick()
  popperJS = createPopper(targetToggle.value, tooltip.value, {
    placement: props.placement,
    modifiers: [
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false, // true by default
          adaptive: false,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      {
        name: 'flip',
      },
    ],
  })
  document.addEventListener('keydown', keydownEscape)
  document.addEventListener('click', handleDocumentClick)
})

const keydownEscape = (e) => {
  if (e.key === 'Escape') {
    close()
  }
}

const toggle = () => {
  if (!isActive.value) {
    isActive.value = true
    nextTick(() => {
      popperJS.update()
      // TODO 画面の初回トリガーでスクロールが発生する不具合への対応
      // focusFirst(tooltip.value)
    })
  } else {
    close()
  }
}

const close = () => {
  if (isActive.value) {
    isActive.value = false
    if (targetToggle.value) {
      targetToggle.value.focus()
    }
  }
}

const handleDocumentClick = (e: any) => {
  if (!tooltip.value || elementContains(tooltip.value, e.target)) {
    return
  } else if (targetToggle.value && elementContains(targetToggle.value, e.target)) {
    return
  }
  close()
}

const elementContains = (elm: HTMLElement, otherElm: HTMLElement) => {
  if (typeof elm.contains === 'function') {
    return elm.contains(otherElm)
  }
  return false
}

onUnmounted(() => {
  document.removeEventListener('keydown', keydownEscape)
  document.removeEventListener('click', handleDocumentClick)
  if (popperJS) {
    popperJS.destroy()
    popperJS = null
  }
})
defineExpose({ toggle })
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.trigger {
  cursor: pointer;
}

.jps-tooltip {
  background: $jps-gray-900;
  color: $white;
  padding: 4px;
  border-radius: 4px;
  /* overflow: hidden; hiddenにすると、arrowが消える */
  box-shadow: $component-shadow;
  z-index: 2600;

  /* Arrow */

  .jps-tooltip-arrow,
  .jps-tooltip-arrow::before {
    position: absolute;
    width: 8px;
    height: 8px;
    z-index: -1;
  }

  .jps-tooltip-arrow::before {
    content: '';
    transform: rotate(45deg);
    background: $jps-gray-900;
  }

  &[data-popper-placement^='top'] > .jps-tooltip-arrow {
    bottom: -4px;
  }

  &[data-popper-placement^='bottom'] > .jps-tooltip-arrow {
    top: -4px;
  }

  &[data-popper-placement^='left'] > .jps-tooltip-arrow {
    right: -4px;
  }

  &[data-popper-placement^='right'] > .jps-tooltip-arrow {
    left: -4px;
  }
}
</style>
